import { useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useProjects } from "../hooks/useProjects";
import { useBoards } from "../hooks/useBoards";
import { useTasks } from "../hooks/useTasks";
import { useUsers } from "../hooks/useUsers";
import { useModal } from "../hooks/useModal";
import { useFilterAndSort } from "../hooks/useFilterAndSort";
import { useNotifications } from "../hooks/useNotifications";
import Board from "../components/Board";
import TaskModal from "../components/TaskModal";
import "../styles/board.css";
import { markTaskDone } from "../redux/slices/tasksSlice";
import { useDispatch } from "react-redux";
import { useUndo } from "../hooks/useUndo";
import { useBulkActions } from "../hooks/useBulkActions"; // ⭐ NEW

const BoardView = () => {
  const dispatch = useDispatch();
  const { record, undo, canUndo } = useUndo();

  const { selectedTasks, toggleSelect, clearSelection } = useBulkActions(); // ⭐ NEW

  const { id: projectId } = useParams();
  const { isAuthenticated } = useAuth();
  const { projects, setSelectedProject, selectedProject } = useProjects();
  const { boards, createBoard } = useBoards(projectId);
  const { tasks, createTask, editTask, removeTask, moveTaskToBoard } = useTasks(projectId);
  const { users } = useUsers();
  const taskModal = useModal();
  const { success } = useNotifications();

  const {
    search,
    priority,
    assigneeId,
    sortBy,
    setSearch,
    setPriority,
    setAssigneeId,
    setSortBy,
    filteredTasks,
  } = useFilterAndSort(tasks);

  if (!isAuthenticated) return <div className="page-container"><h2>Please login first.</h2></div>;

  if (!selectedProject || selectedProject.id !== projectId) {
    const found = projects.find((p) => p.id === projectId);
    if (found) setSelectedProject(projectId);
  }

  const handleAddBoard = () => {
    const title = window.prompt("Board name?");
    if (title) {
      createBoard(title);
      success("Board created");
    }
  };

  const handleAddTask = (boardId) => taskModal.open({ boardId, mode: "create" });
  const handleTaskClick = (task) => taskModal.open({ ...task, mode: "edit" });

  const handleSaveTask = (formData) => {
    const payload = taskModal.payload;

    if (payload.mode === "create") {
      createTask({ ...formData, boardId: payload.boardId });
      success("Task created");
    } else {
      const prevValues = tasks.find((t) => t.id === payload.id);
      record(() => editTask(payload.id, prevValues));
      editTask(payload.id, formData);
      success("Task updated");
    }

    taskModal.close();
  };

  const handleDeleteTask = () => {
    const payload = taskModal.payload;
    const deletedTask = tasks.find((t) => t.id === payload.id);
    record(() => createTask(deletedTask));
    removeTask(payload.id);
    success("Task deleted");
    taskModal.close();
  };

  const handleTaskDone = (taskId) => {
    const prevState = tasks.find((t) => t.id === taskId);
    record(() => dispatch(markTaskDone(prevState)));
    dispatch(markTaskDone(taskId));
    success("Task marked completed");
  };

  const handleMoveTask = (taskId, targetBoardId) => {
    const prevBoard = tasks.find((t) => t.id === taskId)?.boardId;
    record(() => moveTaskToBoard(taskId, prevBoard));
    moveTaskToBoard(taskId, targetBoardId);
  };

  const tasksByBoard = (boardId) => filteredTasks.filter((t) => t.boardId === boardId);

  return (
    <div className="page-container">
      <div className="page-header board-header-main">
        <div>
          <h1>{selectedProject?.name || "Project"}</h1>
          {selectedProject?.description && <p className="project-subtitle">{selectedProject.description}</p>}
        </div>

        <div className="board-actions">
          <button className="btn-secondary" onClick={handleAddBoard}>+ Board</button>

          <button className="btn-secondary" disabled={!canUndo} onClick={undo}>
            ⤺ Undo
          </button>

          {/* ⭐ BULK DELETE BUTTON */}
          {selectedTasks.length > 0 && (
            <button
              className="btn-danger"
              onClick={() => {
                selectedTasks.forEach((id) => removeTask(id));
                clearSelection();
                success("Selected tasks deleted");
              }}
            >
              🗑 Delete Selected ({selectedTasks.length})
            </button>
          )}
        </div>
      </div>

      <div className="filters-bar">
        <input placeholder="Search tasks..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="All">All priorities</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
          <option value="All">All assignees</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="none">No sorting</option>
          <option value="dueDate">Sort by due date</option>
          <option value="priority">Sort by priority</option>
        </select>
      </div>

      <div className="boards-container">
        {boards.map((board) => (
          <Board
            key={board.id}
            board={board}
            tasks={tasksByBoard(board.id)}
            users={users}
            onTaskClick={handleTaskClick}
            onDropTask={handleMoveTask}
            onAddTask={handleAddTask}
            onTaskDone={handleTaskDone}
            toggleSelect={toggleSelect}        // ⭐ required for bulk select
            selectedTasks={selectedTasks}      // ⭐ required to highlight
          />
        ))}
      </div>

      <TaskModal
        isOpen={taskModal.isOpen}
        onClose={taskModal.close}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
        users={users}
        initialTask={taskModal.payload?.mode === "edit" ? taskModal.payload : null}
      />
    </div>
  );
};

export default BoardView;
